import React, { createContext, useMemo } from "react";
import {
  useCoursesQuery,
  useCourseTracksQuery,
  useBlogPostsQuery,
  useOurServicesQuery,
  useOurJobsQuery,
  useLandingBundleQuery,
} from "../hooks/queries/useCourseQueries";

// 16 Legacy Contexts preserved with identical names & types for 100% backward compatibility
export const ServiceContext = createContext<any>(null);
export const CompanyInfoContext = createContext<any>(null);
export const JobContext = createContext<any>(null);
export const CategoryContext = createContext<any>(null);
export const ClientContext = createContext<any>(null);
export const CoreValuesContext = createContext<any>(null);
export const TechContext = createContext<any>(null);
export const IndustryContext = createContext<any>(null);
export const WorkProcessContext = createContext<any>(null);
export const TeamContext = createContext<any>(null);
export const TestimonialContext = createContext<any>(null);
export const JobOpeningContext = createContext<any>(null);
export const CourseTrackContext = createContext<any>(null);
export const CourseContext = createContext<any>(null);
export const CourseHeroContext = createContext<any>(null);
export const BlogContext = createContext<any>(null);

export interface AppStateBridgeProps {
  children: React.ReactNode;
}

/**
 * Composite bridge provider component that connects TanStack Query server-state
 * (with configured staleTime: 6h, gcTime: 8h) to legacy React Contexts.
 * 
 * Prevents remount waterfalls, provides stable memoized values, and eliminates
 * provider clutter in App.tsx while guaranteeing downstream compatibility.
 */
export const AppStateBridge: React.FC<AppStateBridgeProps> = ({ children }) => {
  // TanStack Query server-state hooks with automatic caching and request deduplication
  const { data: courses = [], isLoading: courseLoading } = useCoursesQuery();
  const { data: tracks = [], isLoading: trackLoading } = useCourseTracksQuery();
  const { data: blog = [], isLoading: blogLoading } = useBlogPostsQuery();
  const { data: service = [], isLoading: serviceLoading } = useOurServicesQuery();
  const { data: jobs = [], isLoading: jobLoading } = useOurJobsQuery();
  const { data: landingBundle } = useLandingBundleQuery();

  // Extract categories memoized from jobs
  const categories = useMemo(() => {
    if (!Array.isArray(jobs)) return [];
    return jobs.reduce((acc: any[], product: any) => {
      const productCategories = Array.isArray(product?.category)
        ? product.category
        : [];
      productCategories.forEach((category: any) => {
        const existingCategory = acc.find((c) => c?.name === category?.name);
        if (!existingCategory) {
          acc.push(category);
        }
      });
      return acc;
    }, []);
  }, [jobs]);

  // Safely extract slices from landingBundle
  const testimonials = landingBundle?.testimonials ?? [];
  const ourTeam = landingBundle?.ourTeam ?? [];
  const workProcess = landingBundle?.workProcess ?? [];
  const industry = landingBundle?.industry ?? [];
  const tech = landingBundle?.tech ?? [];
  const coreValue = landingBundle?.coreValue ?? [];
  const client = landingBundle?.client ?? [];
  const companyInfo = landingBundle?.companyInfo ?? {};
  const jobOpenings = landingBundle?.jobOpenings ?? [];
  const courseHero = landingBundle?.courseHero ?? {};

  // Memoize composite provider objects to prevent re-render cascading
  const serviceValue = useMemo(() => ({ service, serviceLoading }), [service, serviceLoading]);
  const jobValue = useMemo(() => ({ jobs, jobLoading }), [jobs, jobLoading]);
  const trackValue = useMemo(() => ({ tracks, trackLoading }), [tracks, trackLoading]);
  const courseValue = useMemo(() => ({ courses, courseLoading }), [courses, courseLoading]);
  const blogValue = useMemo(() => ({ blog, blogLoading }), [blog, blogLoading]);

  return (
    <ServiceContext.Provider value={serviceValue}>
      <CompanyInfoContext.Provider value={companyInfo}>
        <JobContext.Provider value={jobValue}>
          <CategoryContext.Provider value={categories}>
            <ClientContext.Provider value={client}>
              <CoreValuesContext.Provider value={coreValue}>
                <TechContext.Provider value={tech}>
                  <IndustryContext.Provider value={industry}>
                    <WorkProcessContext.Provider value={workProcess}>
                      <TeamContext.Provider value={ourTeam}>
                        <TestimonialContext.Provider value={testimonials}>
                          <JobOpeningContext.Provider value={jobOpenings}>
                            <CourseTrackContext.Provider value={trackValue}>
                              <CourseContext.Provider value={courseValue}>
                                <CourseHeroContext.Provider value={courseHero}>
                                  <BlogContext.Provider value={blogValue}>
                                    {children}
                                  </BlogContext.Provider>
                                </CourseHeroContext.Provider>
                              </CourseContext.Provider>
                            </CourseTrackContext.Provider>
                          </JobOpeningContext.Provider>
                        </TestimonialContext.Provider>
                      </TeamContext.Provider>
                    </WorkProcessContext.Provider>
                  </IndustryContext.Provider>
                </TechContext.Provider>
              </CoreValuesContext.Provider>
            </ClientContext.Provider>
          </CategoryContext.Provider>
        </JobContext.Provider>
      </CompanyInfoContext.Provider>
    </ServiceContext.Provider>
  );
};
